import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Field label that reveals its description on hover instead of printing it
 * as an always-present paragraph (which ate vertical space).
 *
 * When a description exists the label text itself becomes the trigger. At rest
 * it's visually identical to a plain label (no persistent decoration, so it
 * doesn't add noise to the form); on hover/focus a subtle dotted underline
 * fades in and the cursor turns to `help`, signalling there's more to read.
 * This keeps every described field looking identical regardless of label
 * length, rather than trailing a misaligned icon after each name. Undescribed
 * fields render a plain, focus-linked <Label>.
 * @param props - Label id, text, required/description flags, and render variant
 * @returns The rendered field label element
 */
export function FieldLabel({
  id,
  label,
  required,
  description,
  variant = "field",
  calc = false,
}: {
  id?: string;
  label: string;
  required?: boolean;
  description?: string;
  /**
   * "field" (default) — a normal leaf-field label.
   * "header" — a section/container title (array, object, group). Rendered as an
   * uppercase, letter-spaced eyebrow so it reads as a heading rather than just
   * another field, giving the form visual hierarchy.
   */
  variant?: "field" | "header";
  /** Read-only calculation field — italic + muted to read as derived, not editable. */
  calc?: boolean;
}) {
  const labelCls = cn(
    variant === "header"
      ? "text-xs font-bold uppercase tracking-wider text-foreground/70"
      : "text-sm font-normal leading-none",
    calc && variant === "field" && "italic text-muted-foreground",
  );

  const requiredMark = required ? (
    <span className="text-destructive ml-0.5">*</span>
  ) : null;

  if (!description) {
    return (
      <Label htmlFor={id} className={labelCls}>
        {label}
        {requiredMark}
      </Label>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={-1}
            aria-label={`${label}, show description`}
            className={cn(
              "cursor-help text-left underline decoration-dotted decoration-transparent underline-offset-4 transition-colors hover:decoration-muted-foreground/50 focus-visible:outline-none focus-visible:decoration-muted-foreground/50",
              labelCls,
            )}
          >
            {label}
            {requiredMark}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="w-64 text-xs leading-snug"
        >
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
